// Single source of truth for every tool's alternate search terms — glued
// compound words (jsonparser), short acronyms/shorthand (jwt), and common
// action phrases (compare json, generate password). Three things are
// derived from this one list rather than hand-maintained separately:
//   1. ALIAS_REDIRECTS — path-safe (no-space) redirect routes, consumed by
//      App.tsx to send e.g. /jsonparser or /json-diff-checker to the real
//      canonical page via <Navigate replace>, so a typo'd or glued URL
//      never 404s.
//   2. findToolPath() — a small lookup "engine": given any free-typed
//      query (any casing, spaces or hyphens), returns the canonical route
//      it resolves to, or null. Powers the redirects above and is
//      available for a future on-site search box without needing a
//      second copy of this data.
//   3. ToolLayout reads this by path and folds the raw terms into the
//      page's <meta name="keywords"> alongside its own curated keywords
//      string, so every alias/phrase here also becomes part of each
//      page's SEO metadata without editing all 35 pages individually.
//
// Multi-word phrases here (e.g. "online json parser") are NOT turned into
// routes as literal multi-segment paths — they're hyphenated into a single
// slug instead ("online-json-parser"), matching how a real person would
// actually type a URL, and are otherwise used purely as keyword content,
// never as separately indexable pages (that would be a textbook "doorway
// page" pattern search engines penalize) — every alias here is a redirect
// to the one canonical URL, which already carries its own canonical tag.

export interface ToolAliasEntry {
  /** Canonical route, e.g. "/json-parser". */
  path: string;
  /** Alternate search terms: glued words, shorthand, and action phrases. */
  terms: string[];
}

export const TOOL_ALIASES: ToolAliasEntry[] = [
  { path: '/json-parser', terms: ['jsonparser', 'json parse', 'json validator', 'online json parser', 'json view', 'json inspect'] },
  { path: '/json-schema-validator', terms: ['json schema', 'schema validator', 'validate json schema', 'json contract checker'] },
  { path: '/jsonpath-tester', terms: ['jsonpath', 'json path', 'json query', 'json selector'] },
  { path: '/xml-xsd-validator', terms: ['xsd validator', 'xml schema validator', 'validate xsd', 'xml schema checker'] },
  { path: '/json-toml-converter', terms: ['json toml', 'toml converter', 'toml to json', 'configuration converter'] },
  { path: '/url-parser', terms: ['urlparser', 'parse url', 'url inspector', 'url builder'] },
  { path: '/query-string-parser', terms: ['query string', 'query params', 'query builder', 'url parameters'] },
  { path: '/rest-api-tester', terms: ['rest api', 'api tester', 'http client', 'api request'] },
  { path: '/json-beautifier', terms: ['jsonbeautifier', 'json formatter', 'jsonformat', 'prettify json', 'format json online', 'json indent'] },
  { path: '/json-compare', terms: ['jsoncompare', 'json diff', 'compare json', 'json diff checker', 'json delta'] },
  { path: '/text-compare', terms: ['textcompare', 'diff checker', 'text diff', 'compare text online', 'string diff'] },
  { path: '/byte-parser', terms: ['byteparser', 'bytes to mb', 'kb to mb converter', 'byte calculator', 'file size converter'] },
  { path: '/xml-formatter', terms: ['xmlformatter', 'xml beautifier', 'prettify xml', 'xml validator', 'format xml'] },
  { path: '/jwt-decoder', terms: ['jwtdecoder', 'jwt', 'decode jwt', 'jwt inspector', 'jwt parser', 'token decoder', 'bearer token parser'] },
  { path: '/hash-generator', terms: ['hashgenerator', 'md5 generator', 'sha256 generator', 'sha512 online', 'string hash', 'checksum generator'] },
  { path: '/password-generator', terms: ['passwordgenerator', 'generate password', 'api key generator', 'secret key generator', 'secure password maker'] },
  { path: '/password-strength-checker', terms: ['password strength', 'password tester', 'crack time calculator', 'password security checker'] },
  { path: '/certificate-decoder', terms: ['x509 decoder', 'certificate parser', 'pem decoder', 'ssl certificate viewer'] },
  { path: '/iban-validator', terms: ['iban checker', 'iban checksum', 'bank account validator', 'international account number'] },
  { path: '/string-list-tools', terms: ['string replace', 'regex replace', 'list compare', 'compare lists', 'text list diff'] },
  { path: '/qr-code-generator', terms: ['qrcode', 'qr code', 'qr generator', 'barcode qr', 'url qr code'] },
  { path: '/csp-generator', terms: ['cspgenerator', 'content security policy builder', 'generate csp header', 'csp policy maker'] },
  { path: '/aes-encryption', terms: ['aesencryption', 'aes encrypt online', 'aes decrypt', 'aes 256 cipher', 'text encryption key'] },
  { path: '/rsa-encryption', terms: ['rsaencryption', 'rsa key generator', 'rsa encrypt decrypt', 'public private key maker'] },
  { path: '/base64url-converter', terms: ['base64url', 'base32 converter', 'urlsafe base64', 'base64 to text', 'text to base64'] },
  { path: '/encode-decode', terms: ['encodedecode', 'url encode', 'url decode', 'base64 encode online', 'percent encoding'] },
  { path: '/unix-timestamp-converter', terms: ['unixtimestamp', 'epoch converter', 'unix time to date', 'timestamp to utc', 'epoch time calc'] },
  { path: '/timezone-converter', terms: ['timezone', 'time zone converter', 'world clock converter', 'iana timezone'] },
  { path: '/number-byte-formatter', terms: ['byte formatter', 'bytes to gb', 'number format', 'thousand separator'] },
  { path: '/cron-parser', terms: ['cronparser', 'cron expression reader', 'cron schedule builder', 'cron visualizer', 'crontab explain'] },
  { path: '/base-converter', terms: ['baseconverter', 'binary to hex', 'hex to decimal', 'octal converter', 'number base calc'] },
  { path: '/case-converter', terms: ['caseconverter', 'camelcase converter', 'snake case maker', 'kebab case', 'upper case lower case'] },
  { path: '/slug-generator', terms: ['sluggenerator', 'url slug generator', 'string to slug', 'seo title slugify'] },
  { path: '/cidr-calculator', terms: ['cidrcalculator', 'ip subnet calculator', 'cidr range finder', 'subnet mask calc', 'ip block finder'] },
  { path: '/html-entity-encoder', terms: ['htmlentityencoder', 'html escape online', 'unescape html', 'html character codes'] },
  { path: '/graphql-formatter', terms: ['graphqlformatter', 'graphql prettifier', 'format graphql query', 'graphql syntax validator'] },
  { path: '/curl-converter', terms: ['curlconverter', 'curl to fetch', 'curl to python', 'curl to axios', 'convert curl command'] },
  { path: '/sql-formatter', terms: ['sqlformatter', 'format sql', 'sql beautifier', 'mysql query prettifier', 'postgresql formatter'] },
  { path: '/diff-checker', terms: ['diffchecker', 'code compare', 'side by side diff', 'online code diff'] },
  { path: '/yaml-json-converter', terms: ['yamljsonconverter', 'yaml to json', 'json to yaml', 'convert yaml online'] },
  { path: '/user-agent-parser', terms: ['useragentparser', 'parse user agent', 'ua string parser', 'my user agent', 'browser detect'] },
  { path: '/svg-optimizer', terms: ['svgoptimizer', 'svg minifier', 'svg cleaner', 'compress svg online', 'inline svg converter'] },
  { path: '/color-converter', terms: ['colorconverter', 'hex to rgb', 'hex to hsl', 'oklch converter', 'wcag color contrast checker'] },
  { path: '/color-blindness-simulator', terms: ['color blindness', 'color vision simulator', 'protanopia', 'deuteranopia', 'tritanopia'] },
  { path: '/css-effects-generator', terms: ['css gradient', 'gradient generator', 'box shadow', 'box-shadow generator', 'css effects'] },
  { path: '/meta-preview', terms: ['open graph', 'og preview', 'social preview', 'meta tags', 'twitter card'] },
  { path: '/image-resizer', terms: ['image resize', 'image cropper', 'crop image', 'resize image online'] },
  { path: '/image-base64', terms: ['imagebase64', 'image to base64 string', 'base64 to png', 'data uri maker'] },
  { path: '/favicon-generator', terms: ['favicongenerator', 'make favicon', 'ico converter', 'app icon generator', 'apple touch icon maker'] },
  { path: '/guid-generator', terms: ['guidgenerator', 'uuid generator', 'v4 uuid maker', 'generate guid online'] },
  { path: '/dummy-data-generator', terms: ['dummydatagenerator', 'mock data maker', 'fake json generator', 'random json builder'] },
  { path: '/lorem-ipsum', terms: ['loremipsum', 'dummy text generator', 'placeholder text maker', 'latin text generator'] },
  { path: '/robotstxt-generator', terms: ['robotstxtgenerator', 'make robots txt', 'googlebot robots txt validator', 'crawler policy maker'] },
];

function slugify(term: string): string {
  return term
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export interface AliasRedirect {
  from: string;
  to: string;
}

// Computed once at module load. Excludes:
//  - a term whose slug is empty, or equals its own tool's canonical path
//    (pointless self-redirect)
//  - a term whose slug equals a DIFFERENT tool's real canonical path (would
//    silently hijack that page's URL)
//  - a term whose slug collides with another tool's alias slug (first
//    occurrence in TOOL_ALIASES order wins; verified by a standalone
//    audit script before this list was written that exactly one such
//    collision exists — Text Compare's "diff checker" vs. the real
//    /diff-checker page — and is dropped here rather than silently winning
//    by declaration order, since the audit found it, not luck)
// Non-tool routes that also must never be shadowed by a generated alias.
const OTHER_APP_PATHS = ['/', '/about', '/privacy-policy'];

export const ALIAS_REDIRECTS: AliasRedirect[] = (() => {
  const canonicalPaths = new Set([...TOOL_ALIASES.map((t) => t.path), ...OTHER_APP_PATHS]);
  const claimedBy = new Map<string, string>();
  const redirects: AliasRedirect[] = [];

  for (const { path, terms } of TOOL_ALIASES) {
    for (const term of terms) {
      const slug = slugify(term);
      const from = `/${slug}`;
      if (!slug || from === path || canonicalPaths.has(from)) continue;

      const owner = claimedBy.get(slug);
      if (owner !== undefined) {
        if (owner !== path) continue; // claimed by a different tool -- drop
        continue; // duplicate phrasing within the same tool -- already added
      }
      claimedBy.set(slug, path);
      redirects.push({ from, to: path });
    }
  }
  return redirects;
})();

const KEYWORDS_BY_PATH: Map<string, string> = new Map(
  TOOL_ALIASES.map(({ path, terms }) => [path, terms.join(', ')])
);

/** Extra keyword terms for a route, for folding into a page's own curated `keywords` string. */
export function getAliasKeywords(path: string): string | undefined {
  return KEYWORDS_BY_PATH.get(path);
}

/**
 * The "keyword lookup engine": resolves any free-typed query (any casing,
 * spaces or hyphens) to the canonical route it refers to, checking both
 * real canonical paths and every alias term. Returns null if nothing
 * matches. Powers ALIAS_REDIRECTS above and is ready to back an on-site
 * search box later without a second data source.
 */
export function findToolPath(query: string): string | null {
  const slug = slugify(query);
  if (!slug) return null;
  const from = `/${slug}`;
  if (TOOL_ALIASES.some((t) => t.path === from)) return from;
  const hit = ALIAS_REDIRECTS.find((r) => r.from === from);
  return hit ? hit.to : null;
}
